import { useState, useEffect, useRef } from "react";
import globalSearchApi from "../api/globalSearchApi";
import diagnosticSearchApi from "../api/diagnosticSearchApi";
import { searchSettingsIndex } from "../components/GlobalSearch/settingsSearchIndex";
import { searchActionsIndex } from "../components/GlobalSearch/actionsSearchIndex";
import { searchModuleIndex } from "../components/GlobalSearch/moduleSearchIndex";
import { getRoles } from "../util/getRoles";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

// Cheapest reasonable heuristic for "this looks like a diagnostic question,
// not just a name/keyword lookup" - a plain substring/keyword match, same
// no-AI spirit as the diagnostic intent matcher itself
// (server/controller/diagnosticSearch.js). Deliberately loose (false
// positives just mean one extra parallel API call that comes back null) -
// false NEGATIVES are the one to avoid, since those silently never show the
// Answer group at all.
// Exported so GlobalSearch/index.js can reuse the exact same word list for
// its own "does this look diagnostic-shaped" check (the empty-state teaching
// moment) instead of maintaining a second, possibly-drifting regex. That
// call site intentionally skips the DIAGNOSTIC_MIN_QUERY_LENGTH gate below -
// this export is the shared heuristic, the length gate is specific to
// deciding whether to fire the paid backend call.
export const DIAGNOSTIC_QUERY_HINT = /\b(why|reason|who|status|active|inactive|employed|pending|approved|accepted|rejected|cancel|penalty|wrong|mismatch|not showing|still|missing|absent|late|early|half day|overtime|regulari[sz]|shift|comp.?off|wfh|claim|expense|probation|confirm|acknowledg|sandwich|department|branch|designation|manager)\b/i;
// A diagnostic question is a short sentence, not a bare name/keyword - require
// a minimum length so e.g. typing just "rohan" never fires this second call.
const DIAGNOSTIC_MIN_QUERY_LENGTH = 8;

const EMPTY_RESULTS = {
  employees: { count: 0, items: [] },
  leaveRequests: { count: 0, items: [] },
  attendanceRecords: { count: 0, items: [] },
  holidays: { count: 0, items: [] },
  policies: { count: 0, items: [] },
};

// "actions", "settings" and "modules" are all purely client-side indexes (no
// API call) - computed fresh on every keystroke regardless of query length/
// debounce state.
const buildClientSideResults = (query) => {
  const settings = searchSettingsIndex(query);
  const actions = searchActionsIndex(query);
  const modules = searchModuleIndex(query);
  return {
    settings: { count: settings.length, items: settings },
    actions: { count: actions.length, items: actions },
    modules: { count: modules.length, items: modules },
  };
};

/**
 * Debounced global search across server-backed categories (employees, leave
 * requests, attendance records, holidays, policies) plus purely client-side
 * "settings", "actions" and "modules" categories. Server-backed categories
 * fire only once the query is at least `MIN_QUERY_LENGTH` chars.
 */
const useGlobalSearch = (query) => {
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Admin-only diagnostic FAQ answer (see diagnosticSearchApi.js /
  // server/controller/diagnosticSearch.js) - null whenever the viewer isn't
  // Admin, the query doesn't look like a diagnostic question, or the backend
  // found no matching intent/employee. Kept separate from `results` since it
  // isn't one of the regular record categories (not a navigable row list).
  const [diagnosticAnswer, setDiagnosticAnswer] = useState(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const clientSide = buildClientSideResults(query);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults({ ...EMPTY_RESULTS, ...clientSide });
      setDiagnosticAnswer(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    const currentRequestId = ++requestIdRef.current;

    // Client-side gate first (never even fire the call for a non-Admin), the
    // server's hard Admin-only gate is defense-in-depth on top of this, not
    // the only gate.
    const shouldFireDiagnostic =
      getRoles(["Admin"]) && trimmed.length >= DIAGNOSTIC_MIN_QUERY_LENGTH && DIAGNOSTIC_QUERY_HINT.test(trimmed);

    debounceRef.current = setTimeout(async () => {
      try {
        const [response, diagnosticResponse] = await Promise.all([
          globalSearchApi.search(trimmed),
          // diagnosticSearchApi.search already never rejects (timeout/network
          // failures resolve to a 403-shaped envelope, see diagnosticSearchApi.js) -
          // this `.catch(() => null)` is belt-and-suspenders on top of that, so a
          // diagnostic-search failure of ANY kind can never reject this Promise.all
          // and take the main global-search half down with it.
          shouldFireDiagnostic ? diagnosticSearchApi.search(trimmed).catch(() => null) : Promise.resolve(null),
        ]);
        if (currentRequestId !== requestIdRef.current) return; // stale response, a newer query superseded it
        // Repo convention: success is status === 200 (configs.success_status), not 1.
        if (response?.status !== 200) throw new Error(response?.message || "Search failed");
        const data = response?.data || {};
        setResults({
          employees: data.employees || EMPTY_RESULTS.employees,
          leaveRequests: data.leaveRequests || EMPTY_RESULTS.leaveRequests,
          attendanceRecords: data.attendanceRecords || EMPTY_RESULTS.attendanceRecords,
          holidays: data.holidays || EMPTY_RESULTS.holidays,
          policies: data.policies || EMPTY_RESULTS.policies,
          ...clientSide,
        });
        // A 403 (non-Admin) or a null `data` (no matching intent/employee)
        // both just mean "no Answer group this time" - never surfaced as an
        // error, since firing this call was always a best-effort guess.
        setDiagnosticAnswer(diagnosticResponse?.status === 200 && diagnosticResponse?.data ? diagnosticResponse.data : null);
      } catch (err) {
        // Backend endpoint may not be live yet / network hiccup - fail soft so the
        // dropdown still shows the client-side Settings/Actions matches instead of crashing.
        if (currentRequestId !== requestIdRef.current) return;
        setError(err.message || "Something went wrong");
        setResults({ ...EMPTY_RESULTS, ...clientSide });
        setDiagnosticAnswer(null);
      } finally {
        if (currentRequestId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  return { results, loading, error, diagnosticAnswer };
};

export default useGlobalSearch;
